import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  Pagination,
  Grid,
  Alert,
  CircularProgress,
  Tooltip
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Visibility as ViewIcon,
  Search as SearchIcon,
  FilterList as FilterIcon
} from '@mui/icons-material';
import { useDispatch, useSelector } from 'react-redux';
import { getChitGroups, createChitGroup } from '../redux/actions/chitActions';

const ChitGroups = () => {
  const dispatch = useDispatch();
  const { chitGroups, chitGroupsLoading, chitGroupsError, chitGroupsPagination } = useSelector(state => state.chits);
  const { loading, error, success } = useSelector(state => state.chits);

  const [openDialog, setOpenDialog] = useState(false);
  const [editingGroup, setEditingGroup] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterActive, setFilterActive] = useState('true');
  const [page, setPage] = useState(1);
  const [formData, setFormData] = useState({
    group_name: '',
    chit_amount: '',
    duration_months: 12,
    monthly_installment: '',
    bonus_amount: '',
    max_participants: 100,
    description: ''
  });

  useEffect(() => {
    fetchChitGroups();
  }, [page, searchTerm, filterActive]);

  const fetchChitGroups = () => {
    dispatch(getChitGroups({
      page,
      limit: 10,
      search: searchTerm,
      is_active: filterActive
    }));
  };

  const handleOpenDialog = (group = null) => {
    if (group) {
      setEditingGroup(group);
      setFormData({
        group_name: group.group_name,
        chit_amount: group.chit_amount,
        duration_months: group.duration_months,
        monthly_installment: group.monthly_installment,
        bonus_amount: group.bonus_amount,
        max_participants: group.max_participants,
        description: group.description || ''
      });
    } else {
      setEditingGroup(null);
      setFormData({
        group_name: '',
        chit_amount: '',
        duration_months: 12,
        monthly_installment: '',
        bonus_amount: '',
        max_participants: 100,
        description: ''
      });
    }
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setEditingGroup(null);
    setFormData({
      group_name: '',
      chit_amount: '',
      duration_months: 12,
      monthly_installment: '',
      bonus_amount: '',
      max_participants: 100,
      description: ''
    });
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await dispatch(createChitGroup(formData));
      handleCloseDialog();
      fetchChitGroups();
    } catch (error) {
      console.error('Error creating chit group:', error);
    }
  };

  const handlePageChange = (event, newPage) => {
    setPage(newPage);
  };

  const handleSearch = (e) => {
    setSearchTerm(e.target.value);
    setPage(1);
  };

  const handleFilterChange = (e) => {
    setFilterActive(e.target.value);
    setPage(1);
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR'
    }).format(amount);
  };

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4" component="h1">
          Chit Groups
        </Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => handleOpenDialog()}
        >
          Add Chit Group
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mb: 2 }}>
          {success}
        </Alert>
      )}

      <Card>
        <CardContent>
          <Box display="flex" gap={2} mb={3}>
            <TextField
              placeholder="Search chit groups..."
              value={searchTerm}
              onChange={handleSearch}
              InputProps={{
                startAdornment: <SearchIcon sx={{ mr: 1, color: 'text.secondary' }} />
              }}
              sx={{ minWidth: 300 }}
            />
            <FormControl sx={{ minWidth: 150 }}>
              <InputLabel>Status</InputLabel>
              <Select
                value={filterActive}
                onChange={handleFilterChange}
                label="Status"
              >
                <MenuItem value="true">Active</MenuItem>
                <MenuItem value="false">Inactive</MenuItem>
                <MenuItem value="">All</MenuItem>
              </Select>
            </FormControl>
          </Box>

          {chitGroupsLoading ? (
            <Box display="flex" justifyContent="center" p={3}>
              <CircularProgress />
            </Box>
          ) : (
            <>
              <TableContainer component={Paper}>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>Group Name</TableCell>
                      <TableCell>Chit Amount</TableCell>
                      <TableCell>Duration</TableCell>
                      <TableCell>Monthly Installment</TableCell>
                      <TableCell>Bonus Amount</TableCell>
                      <TableCell>Participants</TableCell>
                      <TableCell>Status</TableCell>
                      <TableCell>Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {chitGroups.map((group) => (
                      <TableRow key={group.id}>
                        <TableCell>
                          <Typography variant="subtitle2" fontWeight="bold">
                            {group.group_name}
                          </Typography>
                          {group.description && (
                            <Typography variant="caption" color="text.secondary">
                              {group.description}
                            </Typography>
                          )}
                        </TableCell>
                        <TableCell>
                          <Typography variant="subtitle2" fontWeight="bold">
                            {formatCurrency(group.chit_amount)}
                          </Typography>
                        </TableCell>
                        <TableCell>{group.duration_months} months</TableCell>
                        <TableCell>{formatCurrency(group.monthly_installment)}</TableCell>
                        <TableCell>{formatCurrency(group.bonus_amount)}</TableCell>
                        <TableCell>
                          {group.current_participants} / {group.max_participants}
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={group.is_active ? 'Active' : 'Inactive'}
                            color={group.is_active ? 'success' : 'default'}
                            size="small"
                          />
                        </TableCell>
                        <TableCell>
                          <Tooltip title="View Details">
                            <IconButton size="small">
                              <ViewIcon />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Edit">
                            <IconButton size="small" onClick={() => handleOpenDialog(group)}>
                              <EditIcon />
                            </IconButton>
                          </Tooltip>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>

              {chitGroupsPagination && chitGroupsPagination.pages > 1 && (
                <Box display="flex" justifyContent="center" mt={3}>
                  <Pagination
                    count={chitGroupsPagination.pages}
                    page={page}
                    onChange={handlePageChange}
                    color="primary"
                  />
                </Box>
              )}
            </>
          )}
        </CardContent>
      </Card>

      {/* Add/Edit Dialog */}
      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="md" fullWidth>
        <DialogTitle>
          {editingGroup ? 'Edit Chit Group' : 'Add New Chit Group'}
        </DialogTitle>
        <form onSubmit={handleSubmit}>
          <DialogContent>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Group Name"
                  name="group_name"
                  value={formData.group_name}
                  onChange={handleInputChange}
                  required
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Chit Amount"
                  name="chit_amount"
                  type="number"
                  value={formData.chit_amount}
                  onChange={handleInputChange}
                  required
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Duration (Months)"
                  name="duration_months"
                  type="number"
                  value={formData.duration_months}
                  onChange={handleInputChange}
                  required
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Monthly Installment"
                  name="monthly_installment"
                  type="number"
                  value={formData.monthly_installment}
                  onChange={handleInputChange}
                  required
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Bonus Amount"
                  name="bonus_amount"
                  type="number"
                  value={formData.bonus_amount}
                  onChange={handleInputChange}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Max Participants"
                  name="max_participants"
                  type="number"
                  value={formData.max_participants}
                  onChange={handleInputChange}
                  required
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Description"
                  name="description"
                  multiline
                  rows={3}
                  value={formData.description}
                  onChange={handleInputChange}
                />
              </Grid>
            </Grid>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseDialog}>Cancel</Button>
            <Button type="submit" variant="contained" disabled={loading}>
              {loading ? <CircularProgress size={20} /> : 'Save'}
            </Button>
          </DialogActions>
        </form>
      </Dialog>
    </Box>
  );
};

export default ChitGroups;
